<template>
  <container class="bg-white">
    <DxGridWithSearch
      :key="type"
      :title="title"
      keyExpr="id"
      :dataSource="dataSource"
      :toolbar="toolbar"
      :gridName="type"
      :columns="columns"
      :selection="selection"
    >
      <template v-slot:expirationCell="{ data }">
        <ExpiryTemplate :expiry-date="data.value" />
      </template>
    </DxGridWithSearch>
    <modal :status="isModalOpen" @close="isModalOpen = false">
      <form
        v-shortkey="shortkey"
        @shortkey="handleSubmit"
        @submit.prevent="handleSubmit"
        class="form container"
      >
        <div class="row">
          <h2 class="col-12">
            {{ title }}
          </h2>
        </div>

        <!-- Cytology Diagnostic Codes Fields -->
        <div v-if="type == cytologyEntities.CytDiagnosticCodes" class="row">
          <TextInput class="col-12" label="Name" v-model="form.name" name="name" required />

          <SelectInput
            class="col-6 col-md-3"
            label="Type"
            name="cytTypeId"
            required
            v-model="form.cytTypeId"
            :items="cytologyTypes"
          ></SelectInput>
          <TextInput class="col-6 col-md-3" label="Code" v-model="form.code" name="code" required />
          <SelectInput
            class="col-6 col-md-3"
            label="Code Group"
            required
            v-model="form.cytCodeGroupId"
            name="cytCodeGroupId"
            :items="cytCodeGroups"
          />
          <SelectInput
            class="col-6 col-md-3"
            label="Review Type"
            v-model="form.cytReviewTypeId"
            required
            name="cytReviewTypeId"
            :items="cytReviewTypes"
          />
          <TagInput
            class="col-6 col-md-4"
            v-model="form.icdCodes"
            :dataSource="icdCodesStore"
            searchExpr="displayName"
            name="icdCodes"
          />
          <NumberInput
            class="col-6 col-md-3"
            label="Severity"
            min="0"
            default="0"
            v-model="form.severityLevel"
            name="severityLevel"
            required
          />
          <SelectInput
            class="col-6 col-md-3"
            label="Discrepancy Notification"
            name="discrepancyNotification"
            required
            v-model="form.discrepancyNotification"
            :items="yesNo"
          >
          </SelectInput>
          <div class="col-12 mt-4">
            <h3>Formatted Text</h3>
            <BaseEditor required v-model="form.formattedText" name="formattedText" />
          </div>
        </div>

        <!-- Cytology Questions Fields -->
        <div v-if="type == cytologyEntities.CytQuestions" class="row">
          <NumberInput
            class="col-6"
            label="Sequence Number"
            v-model="form.seqNum"
            default="1"
            name="seqNum"
            required
          />
          <SelectInput
            class="col-6"
            label="Question Type"
            name="cytQuestionTypeId"
            v-model="form.cytQuestionTypeId"
            :items="cytQuestionTypes"
            required
          />
          <TextInput
            class="col-12"
            label="Display Text"
            v-model="form.displayText"
            name="displayText"
            required
          />
          <SelectInput
            class="col-3"
            label="Is Response Required"
            name="isResponseRequired"
            v-model="form.isResponseRequired"
            :items="yesNo"
          />
          <fieldset
            v-if="form.cytQuestionTypeId === listQuestionType"
            class="col-9"
            label="Options"
            required
          >
            <legend>Options</legend>
            <dx-data-grid :toolbar="subToolbar" :columns="subCols" :dataSource="form.options" />
            <modal :status="isSubModalOpen" @close="isSubModalOpen = false">
              <form class="container" @submit.prevent="handleSubmitSubForm">
                <div class="row">
                  <h2 class="col-12">Add Option</h2>
                  <TextInput
                    required
                    class="col-8"
                    label="Option Text"
                    v-model="subform.displayName"
                    name="displayName"
                  />
                  <button class="col offset-9 btn btn-primary" type="submit">Save</button>
                </div>
              </form>
            </modal>
          </fieldset>
        </div>

        <!-- Cytology Diagnostic Macros Fields -->
        <div v-if="type == cytologyEntities.CytDiagnosticMacros" class="row">
          <TextInput
            class="col-6"
            label="Display Name"
            v-model="form.displayName"
            name="displayName"
            required
          />
          <SelectInput
            class="col-6"
            label="Type"
            name="cytTypeId"
            v-model="form.cytTypeId"
            :items="cytologyTypes"
          ></SelectInput>
          <TextAreaInput
            class="col-12"
            label="Description"
            v-model="form.description"
            name="cytologyDescription"
            required
          />
          <fieldset class="col-9" label="Questions">
            <legend>Codes</legend>
            <dx-data-grid
              :row-dragging="rowDrag"
              keyExpr="seqNum"
              :toolbar="subToolbar"
              :columns="subCols"
              :dataSource="form.codes"
            />
            <modal :status="isSubModalOpen" @close="isSubModalOpen = false">
              <form class="container" @submit.prevent="handleSubmitSubForm">
                <div class="row">
                  <h2 class="col-12">Add Code</h2>
                  <SelectInput
                    required
                    class="col-10"
                    label="Diagnosis Code"
                    v-model="subform.cytDiagnosticCodeId"
                    name="cytDiagnosticCodeId"
                    displayExpr="displayText"
                    searchExpr="name"
                    :dataSource="mainDiagnosisCodes"
                  />
                  <NumberInput
                    required
                    class="col-2"
                    default="1"
                    label="Sequence"
                    :min="1"
                    v-model="subform.seqNum"
                    name="seqNum"
                  />
                  <button class="col mt-4 ml-auto col-2 btn btn-primary" type="submit">Save</button>
                </div>
              </form>
            </modal>
          </fieldset>
        </div>
        <!-- Cytology Molecular Results Fields && Cytology Molecular Panels Fields -->
        <div
          v-if="
            type == cytologyEntities.CytMolecResults ||
            type == cytologyEntities.CytMolecPanels ||
            type == cytologyEntities.CytMolecTests
          "
          class="row"
        >
          <template v-if="type == cytologyEntities.CytMolecResults">
            <TextInput
              class="col-6"
              required
              label="Display Text"
              v-model="form.displayText"
              name="displayText"
            />
            <SelectInput
              class="col-6"
              label="Result Type"
              name="cytMolecResultTypeId"
              v-model="form.cytMolecResultTypeId"
              :items="cytMolecResultTypes"
              required
            />
          </template>
          <template v-else>
            <TextInput
              class="col-8"
              required
              label="Display Text"
              v-model="form.displayText"
              name="displayText"
            />
          </template>
        </div>

        <!-- Cytology Panels Fields -->
        <div class="row" v-if="type == cytologyEntities.CytMolecPanels">
          <tag-input
            class="col-4"
            label="Transaction Codes"
            v-model="form.billingTransactionCodes"
            name="billingTransactionCodes"
            displayExpr="code"
            :dataSource="billingCodesStore"
          />

          <ICDCodes class="col-12 col-md-6" name="Icd Codes" v-model="form.icdCodes" />
          <fieldset class="col-6" label="Molecular Tests">
            <legend>Tests</legend>
            <dx-data-grid
              :row-dragging="rowDrag"
              keyExpr="seqNum"
              :dataSource="form.tests"
              :toolbar="subToolbar"
              :columns="subCols"
            />
            <modal :status="isSubModalOpen" @close="isSubModalOpen = false">
              <form class="container" @submit.prevent="handleSubmitSubForm">
                <div class="form-row">
                  <h2 class="col-12">Add Test</h2>
                  <SelectInput
                    class="col-6"
                    label="Molecular Results"
                    name="cytMolecTestId"
                    required
                    v-model="subform.cytMolecTestId"
                    :dataSource="molecTestsSearch"
                  />
                  <NumberInput
                    class="col-6"
                    label="Seq Num"
                    v-model="subform.seqNum"
                    default="1"
                    name="seqNum"
                    required
                  />
                  <button class="col offset-11 btn btn-primary" type="submit">Save</button>
                </div>
              </form>
            </modal>
          </fieldset>
        </div>
        <!-- Cytology Triggers Fields -->
        <div class="row" v-if="type == cytologyEntities.CytTriggers">
          <TextInput
            class="col-6"
            label="Description"
            name="description"
            required
            v-model="form.description"
          />
          <TagInput
            class="col-12"
            label="Provider"
            name="providerIds"
            required
            :searchExpr="['firstName', 'lastName']"
            v-model="form.providerIds"
            :displayExpr="providerExpr"
            :dataSource="providersSearch"
          />
          <TagInput
            class="col-6"
            label="Tests To Trigger"
            name="tests"
            required
            displayExpr="displayText"
            v-model="form.tests"
            :dataSource="molecTestsSearch"
          />
          <fieldset class="col-12 row">
            <legend>Codes</legend>
            <SelectInput
              class="col-6"
              label="Diagnostic Code"
              name="cytDiagnosticCodeId"
              v-model="form.cytDiagnosticCodeId"
              :dataSource="cytologyCodesSearch"
              displayExpr="name"
            />
            <SelectInput
              class="col-6"
              label="Molecular Result"
              name="cytMolecResultId"
              v-model="form.cytMolecResultId"
              displayExpr="displayText"
              :dataSource="molecResultsSearch"
            />

            <SelectInput
              class="col-6"
              label="Molecular Test"
              name="cytMolecTestId"
              displayExpr="displayText"
              v-model="form.cytMolecTestId"
              :dataSource="molecTestsSearch"
            />
          </fieldset>

          <fieldset class="col-12 row">
            <legend>Age and Severity</legend>
            <NumberInput
              :showClearButton="true"
              class="col-6"
              label="Min Age"
              v-model="form.minAge"
              name="minAge"
              :default="null"
            />
            <NumberInput
              :showClearButton="true"
              class="col-6"
              label="Max Age"
              v-model="form.maxAge"
              name="maxAge"
              :default="null"
            />
            <NumberInput
              class="col-6"
              label="Min Severity"
              v-model="form.minSeverity"
              :showClearButton="true"
              name="minSeverity"
              :default="null"
            />
            <NumberInput
              class="col-6"
              label="Max Severity"
              :showClearButton="true"
              v-model="form.maxSeverity"
              name="maxSeverity"
              :default="null"
            />

            <NumberInput
              class="col-6"
              label="Wait Months"
              :showClearButton="true"
              v-model="form.waitMonths"
              name="waitMonths"
              :default="null"
            />
          </fieldset>
        </div>
        <!-- Cytology Molecular Tests Fields -->
        <div class="row wrap" v-if="type === cytologyEntities.CytMolecTests">
          <number-input
            class="col-4"
            label="Seq Num"
            v-model="form.seqNum"
            name="seqNum"
            default="1"
            required
          />
          <text-input class="col-4" label="Method" v-model="form.method" name="method" />
          <text-input
            class="col-4"
            label="Composition"
            v-model="form.composition"
            name="composition"
          />
          <select-input
            class="col-4"
            label="Normal Result"
            v-model="form.normalResultId"
            name="normalResultId"
            :dataSource="cytologyResults"
          />
          <tag-input
            class="col-4"
            label="Transaction Codes"
            v-model="form.billingTransactionCodes"
            name="results"
            displayExpr="code"
            :dataSource="billingCodesStore"
          />
          <tag-input
            class="col-4"
            label="Prefixes"
            v-model="form.prefixes"
            name="prefixes"
            displayExpr="code"
            :dataSource="prefixesStore"
          />
          <fieldset class="col-9" label="Responses">
            <legend>Responses</legend>
            <dx-data-grid :toolbar="subToolbar" :columns="subCols" :dataSource="form.results" />
            <modal :status="isSubModalOpen" @close="isSubModalOpen = false">
              <form class="container" @submit.prevent="handleSubmitSubForm">
                <div class="row wrap">
                  <h2 class="col-12">Add Response</h2>
                  <SelectInput
                    class="col-6 col-md-4"
                    label="Molec Result"
                    required
                    name="cytMolecResultId"
                    v-model="subform.cytMolecResultId"
                    :dataSource="molecResultsSearch"
                  />
                  <SelectInput
                    class="col-6 col-md-4"
                    v-model="subform.icdCodeId"
                    searchExpr="displayName"
                    :dataSource="icdCodesStore"
                    label="Icd Code"
                    name="icdCodes"
                  />
                </div>
                <div class="row"></div>
                <div class="row">
                  <div class="col-12">
                    <h3>Explanation Text</h3>
                    <BaseEditor required v-model="subform.explanation" name="explanation" />
                  </div>
                </div>
                <div class="row">
                  <button class="col offset-9 btn btn-primary" type="submit">Save</button>
                </div>
              </form>
            </modal>
          </fieldset>
        </div>
        <div class="row" v-if="hasEffectiveDates">
          <DatePicker
            class="col-6"
            label="Effective Date"
            v-model="form.effectiveOn"
            name="effectiveDate"
          />
          <DatePicker class="col-6" label="Expiry Date" v-model="form.expiryOn" name="expiryOn" />
        </div>
        <div class="row justify-content-end mt-4">
          <button class="btn btn-primary" type="submit">Save</button>
        </div>
      </form>
    </modal>
  </container>
</template>

<script>
import DxGridWithSearch from "@/components/common/DxGridWithSearch.vue";
import cytologyService, { CytologyEntities } from "@/services/cytology.js";
import modal from "@/components/common/Modal.vue";
import {
  CytTypeEnum,
  enumToDropDown,
  CytQuestionTypeEnum,
  CytCodeGroupEnum,
  CytResultTypeEnum,
  AuditLogItems,
  CytReviewTypeEnum,
  CytMolecResultTypeEnum
} from "@/modules/enums";
import DataSource from "devextreme/data/data_source";
import ProviderService from "@/services/providers";
import billingTxCode from "@/services/Billing";
import DropdownApi from "@/services/dropdown";
import Container from "../common/Container.vue";
import {
  createLogComment,
  createLogItem,
  dateRangeFilter,
  removeExtraDivs
} from "@/modules/helpers";
import { AuditLogApi, PrefixApi } from "@/services";
import { cloneDeep } from "lodash";
import { getAltKeys } from "@/modules/helpers";
import { ICDCodes } from "../forms/Selectors";
export default {
  components: {
    DxGridWithSearch,
    modal,
    ExpiryTemplate: () => import("@/components/common/ExpiryTemplate.vue"),
    TextInput: () => import("@/components/common/TextInput.vue"),
    SelectInput: () => import("@/components/common/SelectInput.vue"),
    DatePicker: () => import("@/components/common/DatePicker.vue"),
    TextAreaInput: () => import("@/components/TextAreaInput.vue"),
    NumberInput: () => import("@/components/common/NumberInput.vue"),
    TagInput: () => import("@/components/common/TagInput.vue"),
    DxDataGrid: () => import("devextreme-vue/data-grid"),
    Container,
    ICDCodes,
    BaseEditor: () => import("@/components/common/BaseEditor.vue")
  },
  props: {
    type: {
      type: String,
      required: true
    }
  },
  name: "CytlogyCodesBrowse",
  mounted() {
    this.$on("reload", () => {
      this.dataSource.reload();
    });
  },
  beforeDestroy() {
    this.$off("reload");
  },
  computed: {
    tests() {
      if (this.form.tests) {
        return [...this.form?.tests].sort((a, b) => a.seqNum - b.seqNum);
      }
      return [];
    },
    selection() {
      return {
        mode: "none"
      };
    },
    cytologyResults() {
      const search = cytologyService.createSearch(CytologyEntities.CytMolecResults);
      return new DataSource({
        store: search,
        filter: ["isDeleted", false]
      });
    },

    mainDiagnosisCodes() {
      // const topCodes = [
      //   CytCodeGroupEnum.Adequacy,
      //   CytCodeGroupEnum.Diagnosis,
      //   CytCodeGroupEnum.Comment,
      //   CytCodeGroupEnum.Hormonal,
      //   CytCodeGroupEnum.Recommendation
      // ];
      const filter = [];

      if (this.form.cytTypeId) {
        filter.push("cytTypeId", this.form.cytTypeId);
      }

      return new DataSource({
        store: cytologyService.createSearch(CytologyEntities.CytDiagnosticCodes),
        filter,
        sort: [
          { selector: "cytCodeGroup", desc: false },
          { selector: "name", desc: false }
        ],
        postProcess: items => {
          return items.map(e => ({ ...e, displayText: this.diagCodeDisplay(e) }));
        }
      });
    },
    defaultForm() {
      switch (this.type) {
        case CytologyEntities.CytDiagnosticCodes:
          return {
            icdCodes: [],
            mandatoryModifiers: []
          };
        case CytologyEntities.CytQuestions:
          return {
            options: []
          };
        case CytologyEntities.CytMolecPanels:
          return {
            tests: []
          };
        case CytologyEntities.CytMolecTests:
          return {
            results: []
          };
        default:
          return {};
      }
    },

    toolbar() {
      const result = {
        items: [
          {
            name: "add",
            widget: "dxButton",
            visible: true,
            options: {
              icon: "add",
              onClick: async () => {
                this.isModalOpen = true;
                if (this.type === CytologyEntities.CytQuestions) {
                  // Get next question in sequence;
                  this.form = {
                    effectiveOn: new Date(),
                    ...this.defaultForm,
                    seqNum: await this.getNextQuestionSequence()
                  };
                } else {
                  this.form = {
                    effectiveOn: new Date(),
                    ...this.defaultForm
                  };
                }
                if (this.type === CytologyEntities.CytMolecResults) {
                  this.form.cytMolecResultTypeId = CytMolecResultTypeEnum.Other;
                }
              }
            }
          }
        ]
      };
      if (this.type === CytologyEntities.CytMolecResults) {
        result.items.push({
          name: "showDeleted",
          widget: "dxCheckBox",
          options: {
            text: "Show Deleted",
            value: this.showDeleted,
            onValueChanged: e => {
              this.showDeleted = e.value;
              this.dataSource.reload();
            }
          }
        });
      }

      return result;
    },
    title() {
      switch (this.type) {
        case CytologyEntities.CytDiagnosticCodes:
          return "Cytology Diagnostic Codes ";
        case CytologyEntities.CytDiagnosticMacros:
          return "Cytology Diagnostic Macros";
        case CytologyEntities.CytQuestions:
          return "Cytology Questions";
        case CytologyEntities.CytMolecResults:
          return "Cytology Molecular Results";
        case CytologyEntities.CytMolecPanels:
          return "Cytology Molecular Panels";
        case CytologyEntities.CytTriggers:
          return "Cytology Triggers";
        case CytologyEntities.CytMolecTests:
          return "Cytology Molecular Tests";
        default:
          return "";
      }
    },
    dataSource() {
      const options = {
        store: cytologyService.createSearch(this.type)
      };
      if (this.type === CytologyEntities.CytMolecResults && !this.showDeleted) {
        options.filter = ["isDeleted", false];
      }

      return new DataSource(options);
    },
    subActions() {
      return [
        {
          type: "buttons",
          caption: "Actions",
          buttons: [
            {
              hint: "Edit",
              icon: "edit",
              onClick: this.editSubRow
            },
            {
              hint: "Delete",
              icon: "trash",
              onClick: this.deleteSubRow
            }
          ]
        }
      ];
    },
    subCols() {
      let cols = [];

      switch (this.type) {
        case CytologyEntities.CytMolecTests:
          cols = [
            {
              dataField: "explanation",
              caption: "Explanation",
              encodeHtml: false
            },
            {
              dataField: "cytMolecResultId",
              caption: "Molecular Test",
              lookup: {
                dataSource: this.molecResultsSearch.store(),
                valueExpr: "id",
                displayExpr: "displayText"
              }
            }
          ];
          break;
        case CytologyEntities.CytDiagnosticMacros:
          cols = [
            {
              dataField: "seqNum",
              caption: "Sequence No.",
              sortIndex: 0,
              sortOrder: "asc"
            },
            {
              dataField: "cytDiagnosticCodeId",
              caption: "Diagnostic Code",
              lookup: {
                dataSource: this.cytologyCodesSearch,
                valueExpr: "id",
                displayExpr: "name"
              }
            }
          ];
          break;
        case CytologyEntities.CytMolecPanels:
          cols = [
            {
              dataField: "seqNum",
              caption: "Sequence No.",
              sortIndex: 0,
              sortOrder: "asc"
            },
            {
              dataField: "cytMolecTestId",
              caption: "Molecular Test",
              lookup: {
                dataSource: this.molecTestsSearch,
                valueExpr: "id",
                displayExpr: "displayText"
              }
            }
          ];
          break;
        case CytologyEntities.CytQuestions:
          cols = [
            {
              dataField: "displayName",
              caption: "Option Text"
            }
          ];
          break;

        default:
          cols = [];
          break;
      }
      return [...this.subActions, ...cols];
    },
    actions() {
      const actions = {
        type: "buttons",
        caption: "Actions",
        buttons: [
          {
            hint: "Edit",
            icon: "edit",
            onClick: this.editRow
          }
        ]
      };

      if (
        this.type === CytologyEntities.CytTriggers ||
        this.type === CytologyEntities.CytMolecResults
      ) {
        actions.buttons.push({
          hint: "Delete",
          icon: "trash",
          onClick: this.deleteRow,
          visible: d => {
            if (d?.row?.data) {
              return !d.row.data.isDeleted;
            }
            return false;
          }
        });
        actions.buttons.push({
          hint: "Undelete",
          icon: "redo",
          onClick: this.deleteRow,
          visible: d => {
            if (d?.row?.data) {
              return d.row.data.isDeleted;
            }
            return false;
          }
        });
      }

      return [actions];
    },
    expirationFields() {
      if (this.type === CytologyEntities.CytMolecResults) {
        return [];
      }
      return [
        {
          dataField: "effectiveOn",
          caption: "Effective Date",
          dataType: "date"
        },
        {
          dataField: "expiryOn",
          caption: "Expiry Date",
          dataType: "date",
          cellTemplate: "expirationCell"
        }
      ];
    },
    columns() {
      let cols = [];
      switch (this.type) {
        case CytologyEntities.CytDiagnosticCodes:
          cols = [
            {
              dataField: "name",
              caption: "Name"
            },
            { dataField: "cytType", caption: "Cytology Type" },
            {
              dataField: "cytCodeGroup",
              caption: "Code Group"
            },
            {
              dataField: "code",
              caption: "Code"
            },
            {
              dataField: "severityLevel",
              caption: "Severity Level"
            },
            {
              dataField: "discrepancyNotification",
              caption: "Discrepancy Notification",
              lookup: {
                dataSource: this.yesNo,
                valueExpr: "id",
                displayExpr: "name"
              }
            }
          ];
          break;
        case CytologyEntities.CytDiagnosticMacros:
          cols = [
            {
              dataField: "displayName",
              caption: "Name"
            },
            {
              dataField: "description",
              caption: "Name"
            },
            {
              dataField: "cytType",
              caption: "Cytology Type"
            }
          ];
          break;
        case CytologyEntities.CytQuestions:
          cols = [
            {
              dataField: "seqNum",
              caption: "Sequence Number"
            },
            {
              dataField: "cytQuestionType",
              caption: "Question Type"
            },
            {
              dataField: "displayText",
              caption: "Display Text"
            },
            {
              dataField: "isResponseRequired",
              caption: "Required"
            }
          ];
          break;
        case CytologyEntities.CytTriggers:
          return [
            ...this.actions,

            {
              dataField: "description",
              caption: "Description"
            },
            {
              dataField: "cytDiagnosticCodeId",
              caption: "Diagnostic Code",
              lookup: {
                dataSource: cytologyService.createSearch(CytologyEntities.CytDiagnosticCodes),
                valueExpr: "id",
                displayExpr: "name"
              }
            },
            {
              dataField: "cytMolecResultId",
              caption: "Molecular Result",
              lookup: {
                dataSource: cytologyService.createSearch(CytologyEntities.CytMolecResults),
                valueExpr: "id",
                displayExpr: "displayText"
              }
            },
            {
              dataField: "cytMolecTestId",
              caption: "Molecular Test",
              lookup: {
                dataSource: cytologyService.createSearch(CytologyEntities.CytMolecTests),
                valueExpr: "id",
                displayExpr: "displayText"
              }
            },

            {
              dataField: "minAge"
            },

            {
              dataField: "maxAge"
            },
            {
              dataField: "maxSeverity"
            },
            {
              dataField: "minSeverity"
            },

            {
              dataField: "waitMonths"
            }
          ];
        case CytologyEntities.CytMolecTests:
          cols = [
            { dataField: "displayText", caption: "Name" },
            {
              dataField: "seqNum",
              caption: "Sequence Number"
            },
            {
              dataField: "method",
              caption: "Method"
            },
            { dataField: "composition", caption: "Composition" },
            {
              dataField: "normalResultId",
              caption: "Normal Result",
              lookup: {
                dataSource: this.cytologyResults,
                valueExpr: "id",
                displayExpr: "displayText"
              }
            }
          ];
          break;
        default:
          cols = [{ dataField: "displayText", caption: "Name" }];
          break;
      }
      return [...this.actions, ...cols, ...this.expirationFields];
    },
    hasEffectiveDates() {
      return this.columns.some(c => c.dataField === "effectiveOn");
    }
  },
  data() {
    return {
      showDeleted: false,
      shortkey: getAltKeys("s"),
      rowDrag: {
        allowReordering: true,
        dropFeedbackMode: "push",
        onReorder: this.handleMove
      },
      cytReviewTypes: [
        { id: CytReviewTypeEnum.None, displayName: "None" },
        { id: CytReviewTypeEnum.Reviewer, displayName: "Reviewer" },
        { id: CytReviewTypeEnum.Pathologist, displayName: "Pathologist" }
      ],
      subToolbar: {
        items: [
          {
            name: "add",
            widget: "dxButton",
            options: {
              icon: "add",
              onClick: () => {
                const formState = {};
                const seqNumTypes = [
                  CytologyEntities.CytDiagnosticMacros,
                  CytologyEntities.CytMolecTests,
                  CytologyEntities.CytMolecPanels
                ];
                if (seqNumTypes.includes(this.type)) {
                  //Prepopulate with next sequence number
                  if (this.type === CytologyEntities.CytMolecTests) {
                    formState.seqNum = this.form.results?.length
                      ? Math.max(...this.form.results.map(r => r.seqNum)) + 1
                      : 1;
                  } else if (this.type === CytologyEntities.CytMolecPanels) {
                    formState.seqNum = this.form.tests?.length
                      ? Math.max(...this.form.tests.map(t => t.seqNum)) + 1
                      : 1;
                  } else {
                    // For Macros, use codes
                    const nextSeqNum = this.form.codes?.length
                      ? Math.max(...this.form.codes.map(c => c.seqNum)) + 1
                      : 1;
                    formState.seqNum = nextSeqNum;
                  }
                }
                this.subform = formState;
                this.isSubModalOpen = true;
              }
            }
          }
        ]
      },
      subform: {},
      isSubModalOpen: false,
      cytCodeGroups: enumToDropDown(CytCodeGroupEnum),
      icdCodesStore: new DataSource({
        store: DropdownApi.searchIcdCodes
      }),

      cytologyCodesSearch: cytologyService.createSearch(CytologyEntities.CytDiagnosticCodes),
      molecResultsSearch: new DataSource({
        store: cytologyService.createSearch(CytologyEntities.CytMolecResults),
        filter: ["isDeleted", false]
      }),

      molecTestsSearch: cytologyService.createSearch(CytologyEntities.CytMolecTests),
      billingCodesStore: billingTxCode.searchStore,
      prefixesStore: new DataSource({
        store: PrefixApi.searchStore,
        filter: [...dateRangeFilter(), "and", ["!", ["cytTypeId", CytTypeEnum.Surgical]]],
        sort: ["code"]
      }),
      yesNo: [
        { id: true, name: "Yes" },
        { id: false, name: "No" }
      ],
      cytMolecResultTypes: enumToDropDown(CytMolecResultTypeEnum),
      cytResultType: enumToDropDown(CytResultTypeEnum),
      providersSearch: new DataSource({
        store: ProviderService.searchProviders
      }),
      cytologyEntities: CytologyEntities,
      cytQuestionTypes: enumToDropDown(CytQuestionTypeEnum),
      listQuestionType: CytQuestionTypeEnum.List,
      cytologyTypes: enumToDropDown(CytTypeEnum).filter(e => e.id !== CytTypeEnum.Surgical),

      isEditing: false,
      isModalOpen: false,
      form: {},
      originalDetails: {}
    };
  },
  watch: {
    isSubModalOpen(val) {
      if (!val) {
        this.subform = {};
        this.isEditing = false;
      }
    },
    isModalOpen(val) {
      if (!val) {
        this.form = {};
        this.isEditing = false;
      }
    }
  },
  methods: {
    async getNextQuestionSequence() {
      if (this.type === CytologyEntities.CytQuestions) {
        const maxSeq = await cytologyService.createSearch(this.type).load({
          select: "seqNum",
          sort: [{ selector: "seqNum", desc: true }],
          take: 1
        });
        return maxSeq.length > 0 ? maxSeq[0].seqNum + 1 : 1;
      }
      return null;
    },
    diagCodeDisplay(item) {
      const { cytCodeGroupId, cytCodeGroup, name } = item;

      if (cytCodeGroupId) {
        const modifiers = [CytCodeGroupEnum.AdequacyModifier, CytCodeGroupEnum.DiagnosisModifier];
        if (modifiers.includes(cytCodeGroupId)) {
          return `${name} (${cytCodeGroup[0]}M)`;
        }
        return `${name} (${cytCodeGroup[0]})`;
      }
      return `${item.name}`;
    },
    editSubRow(e) {
      const { row } = e;
      this.subform = row.data;
      this.isEditing = true;
      this.isSubModalOpen = true;
    },
    modifierCodes() {
      const filters = [];

      // Check if form.codes contains modifier codes and get their parent groups
      if (this.form.codes && this.form.codes.length > 0) {
        const parentGroups = new Set();

        this.form.codes.forEach(code => {
          const codeDetails = this.cytologyCodesSearch
            .items()
            .find(item => item.id === code.cytDiagnosticCodeId);
          if (codeDetails) {
            // Check if this is a modifier code and get its parent group
            const modifierEntry = Object.entries(CytCodeGroupEnum).find(
              ([key, value]) => key.includes("Modifier") && value === codeDetails.cytCodeGroupId
            );

            if (modifierEntry) {
              // Get the parent group name by removing 'Modifier' suffix
              const parentGroupName = modifierEntry[0].replace("Modifier", "");
              const parentGroupValue = CytCodeGroupEnum[parentGroupName];
              if (parentGroupValue) {
                parentGroups.add(parentGroupValue);
              }
            }
          }
        });

        // If we have modifier codes, filter by their parent groups only
        if (parentGroups.size > 0) {
          const parentFilters = Array.from(parentGroups).reduce((acc, group, idx) => {
            acc.push(["cytCodeGroupId", "=", group]);
            if (idx < parentGroups.size - 1) {
              acc.push("or");
            }
            return acc;
          }, []);
          filters.push(parentFilters);
        } else {
          // Default filter logic for non-modifier codes
          const topCodes = [
            CytCodeGroupEnum.Adequacy,
            CytCodeGroupEnum.Diagnosis,
            CytCodeGroupEnum.Comment,
            CytCodeGroupEnum.Hormonal,
            CytCodeGroupEnum.Recommendation
          ];

          const topFilters = topCodes.reduce((acc, code, idx) => {
            acc.push(["cytCodeGroupId", "=", code]);
            if (idx < topCodes.length - 1) {
              acc.push("or");
            }
            return acc;
          }, []);
          filters.push(topFilters);
        }
      } else {
        // Default filter logic when no codes are present
        const topCodes = [
          CytCodeGroupEnum.Adequacy,
          CytCodeGroupEnum.Diagnosis,
          CytCodeGroupEnum.Comment,
          CytCodeGroupEnum.Hormonal,
          CytCodeGroupEnum.Recommendation
        ];

        const topFilters = topCodes.reduce((acc, code, idx) => {
          acc.push(["cytCodeGroupId", "=", code]);
          if (idx < topCodes.length - 1) {
            acc.push("or");
          }
          return acc;
        }, []);
        filters.push(topFilters);
      }

      if (this.form.cytTypeId) {
        filters.push("and");
        filters.push(["cytTypeId", "=", this.form.cytTypeId]);
      }

      return new DataSource({
        store: cytologyService.createSearch(CytologyEntities.CytDiagnosticCodes),
        filter: filters
      });
    },
    handleSubmitSubForm() {
      const isNew = !this.subform?.id;

      switch (this.type) {
        case CytologyEntities.CytQuestions:
          {
            const error = this.validateQuestion(this.subform);
            if (error) {
              window.notify(error, "error");
              return;
            }
            if (isNew) {
              this.form.options = [...(this.form.options || []), this.subform];
            } else {
              this.form.options = this.form.options.map(option =>
                option.id === this.subform.id ? this.subform : option
              );
            }
          }
          break;
        case CytologyEntities.CytMolecPanels:
          {
            const error = this.validateMolecPanelTest(this.subform);
            if (error) {
              window.notify(error, "error");
              return;
            }
            if (isNew) {
              this.form.tests = [...(this.form.tests || []), this.subform];
            } else {
              this.form.tests = this.form.tests.map(test =>
                test.id === this.subform.id ? this.subform : test
              );
            }
          }
          break;
        case CytologyEntities.CytMolecTests:
          {
            const error = this.validateMolecResponses(this.subform);
            if (error) {
              window.notify(error, "error");
              return;
            }
            if (isNew) {
              this.form.results = [...(this.form.results || []), this.subform];
            } else {
              this.form.results = this.form.results.map(test =>
                test.id === this.subform.id ? this.subform : test
              );
            }
          }
          break;
        case CytologyEntities.CytDiagnosticMacros:
          {
            const error = this.validateMacroCode(this.subform);
            if (error) {
              window.notify(error, "error");
              return;
            }

            const targetCodes = this.form.codes || [];
            if (isNew) {
              const itemSet = new Set(targetCodes.map(code => code.cytDiagnosticCodeId));
              if (itemSet.has(this.subform.cytDiagnosticCodeId)) {
                window.notify("Code already exists", "error");
                return;
              }
              this.form.codes = [...(targetCodes || []), this.subform];
            } else {
              this.form.codes = targetCodes.map(code =>
                code.id === this.subform.id ? this.subform : code
              );
            }
          }

          break;
        default:
          break;
      }

      this.subform = {};
      this.isSubModalOpen = false;
    },
    validateQuestion(details) {
      const { displayName, id } = details;
      const isNew = !id;
      let current = [...this.form.options];
      if (!isNew) {
        current = this.form.options.filter(e => e.id !== id);
      }

      const isDupeDisplayNameIdx = current.findIndex(option => option.displayName === displayName);
      if (isDupeDisplayNameIdx > -1) {
        return `This option text is already in use.`;
      }

      return null;
    },

    validateMolecResponses(details) {
      const { cytMolecResultId, id, icdCodeId } = details;
      const isNew = !id;
      let current = [...this.form.results];
      if (!isNew) {
        current = this.form.results.filter(e => e.id !== id);
      }
      if (cytMolecResultId) {
        const isDupeResultIdx = current.findIndex(
          test => test.cytMolecResultId === cytMolecResultId
        );
        if (isDupeResultIdx > -1) {
          const dupeItem = this.form.results[isDupeResultIdx];
          if (dupeItem?.id === this.subform?.id) {
            return null;
          }
          return `This result is already in use.`;
        }
      }
      if (icdCodeId) {
        const isDupeIcdCodeIdx = current.findIndex(test => test.icdCodeId === icdCodeId);
        if (isDupeIcdCodeIdx > -1) {
          return `This ICD code is already in use.`;
        }
      }
      return null;
    },
    validateMolecPanelTest(details) {
      const { seqNum, cytMolecTestId, id } = details;
      const isNew = !id;
      let current = [...this.form.tests];
      if (!isNew) {
        current = this.form.tests.filter(e => e.id !== id);
      }
      const maxSeqNum =
        this.form.tests.reduce((max, test) => (Number(test.seqNum) > max ? test.seqNum : max), 0) +
        1;
      if (isNaN(seqNum)) {
        return "Sequence number must be a number";
      }
      const num = Number(seqNum);
      if (num > maxSeqNum) {
        return `Sequence number must be less than the maximum sequence number ${maxSeqNum}`;
      }
      const isDupeSeqNumIdx = current.findIndex(test => test.seqNum === num);
      if (isDupeSeqNumIdx > -1) {
        const dupeItem = this.form.tests[isDupeSeqNumIdx];
        if (dupeItem?.id === this.subform?.id) {
          return null;
        }
        return `This sequence number is already in use`;
      }
      const isDupeResultIdx = current.findIndex(test => test.cytMolecTestId === cytMolecTestId);
      if (isDupeResultIdx > -1) {
        const dupeItem = this.form.tests[isDupeResultIdx];
        if (dupeItem?.id === this.subform?.id) {
          return null;
        }
        return `This result is already in use on sequence number ${dupeItem.seqNum}`;
      }

      return null;
    },

    validateMacroCode(details) {
      const { seqNum, cytDiagnosticCodeId } = details;
      const targetCodes = this.form.codes || [];
      const maxSeqNum =
        targetCodes.reduce((max, code) => (code.seqNum > max ? code.seqNum : max), 0) + 1;
      const isNew = !details.id;
      let current = [...targetCodes];
      if (!isNew) {
        current = targetCodes.filter(e => e.id !== details.id);
      }
      // Check if its a number
      if (isNaN(seqNum)) {
        return "Sequence number must be a number";
      }
      const num = Number(seqNum);
      if (num > maxSeqNum) {
        return `Sequence number must be less than the maximum sequence number ${maxSeqNum}`;
      }
      // Check if seqNum is already in use
      const isDupeSeqNumIdx = current.findIndex(code => Number(code.seqNum) === num);
      if (isDupeSeqNumIdx > -1) {
        return `This sequence number is already in use`;
      }

      const isDupeCodeIdx = current.findIndex(
        code => code.cytDiagnosticCodeId === cytDiagnosticCodeId
      );
      if (isDupeCodeIdx > -1) {
        const { seqNum: seqNumDupe } = targetCodes[isDupeCodeIdx];
        return `Code already exists on sequence number ${seqNumDupe}`;
      }

      return null;
    },
    deleteSubRow(e) {
      const { row } = e;
      const { data } = row;
      switch (this.type) {
        case CytologyEntities.CytQuestions:
          this.form.options = this.form.options.filter(e => e !== data);
          break;
        case CytologyEntities.CytDiagnosticMacros:
          this.form.codes = this.form.codes.filter(e => e !== data);
          break;
        case CytologyEntities.CytMolecPanels:
          this.form.tests = this.form.tests.filter(e => e !== data);
          break;
        case CytologyEntities.CytMolecTests:
          this.form.results = this.form.results.filter(e => e !== data);
          break;
        default:
          break;
      }
    },
    providerExpr(data) {
      if (!data) return "";
      return `${data.firstName} ${data.lastName} (${data.npi})`;
    },
    handleMove(event) {
      const { toIndex, fromIndex } = event;
      if (toIndex === -1) return;
      if (toIndex === fromIndex) return;

      let items = event.component.getVisibleRows();
      if (items.length) {
        items = items.map(e => e.data);
      }

      items.splice(fromIndex, 1);
      items.splice(toIndex, 0, event.itemData);
      switch (this.type) {
        case CytologyEntities.CytMolecPanels:
          this.form.tests = items.map((e, i) => ({
            ...e,
            seqNum: i + 1
          }));
          break;
        case CytologyEntities.CytDiagnosticMacros:
          this.form.codes = items.map((e, i) => ({
            ...e,
            seqNum: i + 1
          }));
          break;
        default:
          break;
      }
    },
    async editRow(e) {
      const { row } = e;
      const {
        data: { id }
      } = row;
      const details = await cytologyService.getSingleEntity(this.type, id);
      this.originalDetails = cloneDeep(details);
      this.form = details;
      this.isModalOpen = true;
    },
    async deleteRow(e) {
      try {
        const { row } = e;
        const {
          data: { id, isDeleted }
        } = row;
        let comment = "";
        if (isDeleted) {
          const confirm = await window.confirm("Are you sure you want to un-delete this record?");
          if (!confirm) return;
          await cytologyService.patchEntity(this.type, { ...row.data, isDeleted: false });
          comment = `Restored the ${this.type} item with ID ${id}. \n ${JSON.stringify(
            row.data,
            null,
            2
          )}`;
        } else {
          const confirm = await window.confirm("Are you sure you want to delete this record?");
          if (!confirm) return;
          await cytologyService.patchEntity(this.type, { ...row.data, isDeleted: true });
          comment = `Deleted the ${this.type} item with ID ${id}. \n ${JSON.stringify(
            row.data,
            null,
            2
          )}`;
        }
        const auditLogMessage = createLogItem({}, AuditLogItems.ChangeAdmin);
        auditLogMessage.comments = comment;
        await AuditLogApi.insertLogMessage(auditLogMessage);
        this.dataSource.reload();
      } catch (error) {
        window.alert("An error occurred while making this change to the record.");
      }
    },
    async handleSubmit() {
      try {
        if (this.form.formattedText) {
          this.form.formattedText = removeExtraDivs(this.form.formattedText);
        }
        //Validate a question sequence number
        if (this.type === CytologyEntities.CytQuestions) {
          const nextSeq = await this.getNextQuestionSequence();
          if (this.form.seqNum > nextSeq) {
            window.notify(`Sequence number must be less than or equal to ${nextSeq}`, "error");
            return;
          }
          // Check if sequence number is already in use
          const isDupeSeqNumIdx = await await cytologyService.createSearch(this.type).load({
            filter: ["seqNum", "=", this.form.seqNum],
            select: "id",
            sort: [{ selector: "seqNum", desc: false }],
            take: 1
          });
          if (isDupeSeqNumIdx.length > 0 && isDupeSeqNumIdx[0].id !== this.form.id) {
            window.notify(`Sequence number ${this.form.seqNum} is already in use`, "error");
            return;
          }
        }
        if (this.form.id) {
          await cytologyService.updateEntity(this.type, this.form);
          const logItem = createLogItem({}, AuditLogItems.ChangeAdmin);
          logItem.comments = createLogComment(this.originalDetails, this.form);
          await AuditLogApi.insertLogMessage(logItem);
        } else {
          await cytologyService.createEntity(this.type, this.form);
          const logItem = createLogItem({}, AuditLogItems.CreateAdmin);
          logItem.comments = createLogComment(JSON.stringify(this.form));
          await AuditLogApi.insertLogMessage(logItem);
        }

        this.isModalOpen = false;
        this.dataSource.reload();
      } catch (error) {
        console.log(error);
        window.notify("An error occurred while saving the record", "error");
      }
    }
  }
};
</script>

<style lang="scss" scoped></style>
